import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Voting.module.css";
import { UI } from "@wwimmo/ui";

type InfoColumn<RowT> = {
    header: string;
    width?: string;
    cell: (row: RowT) => React.ReactNode;
    align?: "left" | "center" | "right";
};

export interface VotingRow {
    id: string;
    label: string;
    selectedRow?: number | null;
    active: boolean;
    unitvalue?: number;
    unitcount?: number;
}

type ColumnLayout = {
    activeWidth?: string; // width for checkbox col
    infoWidths?: string[]; // must match infoColumns length
    voteWidth?: string; // if provided, each vote col uses this width
};

export interface VotingProps<RowT extends VotingRow = VotingRow> {
    infoColumns?: InfoColumn<RowT>[];
    voteHeaders?: string[]; // headers for the vote columns only
    numberOfVoteColumns?: number; // number of vote columns (1..5)
    columnLayout?: ColumnLayout;

    // Left "label" columns are keyed off headers: headers[0] labels secondRowLabels,
    // headers[1] labels thirdRowLabels (when present), the rest are vote headers.
    headers?: string[];
    numberOfColumn?: number;
    rows: RowT[];
    secondRowLabels?: string[];
    thirdRowLabels?: string[];
    withMainRow?: boolean;
    buttonColors?: string[];
    onSelectionChange?: (result: { id: string; selectedColumn: number | null }[]) => void;
    withSearchInput?: boolean;
    onActiveChange?: (id: string, active: boolean) => void;
    zeroUnitField?: "unitvalue" | "unitcount";

    // optional label for main row left cell
    mainRowLeftLabel?: string; // default "Anwesend"
}

const lightenColor = (color: string, percent: number): string => {
    if (!color.startsWith("#")) return color;
    let hex = color.slice(1);
    if (hex.length === 3) {
        hex = hex
            .split("")
            .map((ch) => ch + ch)
            .join("");
    }
    const num = parseInt(hex, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    r = Math.min(255, Math.floor(r + (255 - r) * percent));
    g = Math.min(255, Math.floor(g + (255 - g) * percent));
    b = Math.min(255, Math.floor(b + (255 - b) * percent));
    return `rgb(${r}, ${g}, ${b})`;
};

type VotingRowItemProps<RowT extends VotingRow> = {
    row: RowT;
    fullRowIndex: number;
    voteColumns: number;
    selected: number | null;
    isActive: boolean;
    handleRowVote: (rowIndex: number, colIndex: number) => void;
    getBorderStyle: (colIndex: number, isSelected: boolean) => React.CSSProperties;
    getButtonStyle: (colIndex: number, isSelected: boolean) => React.CSSProperties;
    onActiveChange?: (id: string, active: boolean) => void;
    zeroUnitField?: "unitvalue" | "unitcount";

    infoColumns?: InfoColumn<RowT>[];
    secondLabel?: string;
    thirdLabel?: string;
};

function VotingRowItem<RowT extends VotingRow>(props: VotingRowItemProps<RowT>) {
    const {
        row,
        fullRowIndex,
        voteColumns,
        selected,
        secondLabel,
        thirdLabel,
        isActive,
        handleRowVote,
        getBorderStyle,
        getButtonStyle,
        onActiveChange,
        zeroUnitField = "unitvalue",
        infoColumns
    } = props;

    const isZeroUnit = (row[zeroUnitField] ?? 0) === 0;
    const effectivelyActive = isActive && !isZeroUnit;

    const handleCheckbox = () => {
        onActiveChange?.(row.id, !row.active);
    };

    return (
        <tr>
            {/* Active checkbox cell */}
            <td className={styles.tableCell}>
                <input
                    type="checkbox"
                    checked={row.active}
                    onChange={handleCheckbox}
                    aria-label={`Toggle active for ${row.label}`}
                    className={styles.activeCheckbox}
                />
            </td>

            {/* LEFT AREA */}
            {infoColumns && infoColumns.length > 0 ? (
                infoColumns.map((col, i) => (
                    <td
                        key={`info-${row.id}-${i}`}
                        className={`${styles.rowLabel} ${!effectivelyActive ? styles.disabledCell : ""}`}
                        style={{ textAlign: col.align ?? "left" }}
                    >
                        {col.cell(row)}
                    </td>
                ))
            ) : (
                <>
                    <td className={`${styles.rowLabel} ${!effectivelyActive ? styles.disabledCell : ""}`}>
                        {row.label}
                    </td>

                    {secondLabel !== undefined && (
                        <td className={`${styles.secondRowCell} ${!effectivelyActive ? styles.disabledCell : ""}`}>
                            {secondLabel}
                        </td>
                    )}

                    {thirdLabel !== undefined && (
                        <td className={`${styles.secondRowCell} ${!effectivelyActive ? styles.disabledCell : ""}`}>
                            {thirdLabel}
                        </td>
                    )}
                </>
            )}

            {/* VOTE BUTTONS */}
            {Array.from({ length: voteColumns }).map((_, colIndex) => {
                const isSelected = selected === colIndex;
                return (
                    <td key={`${row.id}-vote-${colIndex}`} className={styles.tableCell}>
                        <div
                            className={`${styles.voteButtonBorder} ${!effectivelyActive ? styles.disabledCell : ""}`}
                            style={getBorderStyle(colIndex, isSelected)}
                            role="button"
                            tabIndex={effectivelyActive ? 0 : -1}
                            onClick={() => effectivelyActive && handleRowVote(fullRowIndex, colIndex)}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            <button
                                type="button"
                                className={`${styles.voteButton} ${isSelected ? styles.active : ""}`}
                                style={isSelected ? getButtonStyle(colIndex, isSelected) : {}}
                                tabIndex={-1}
                                disabled={!effectivelyActive}
                            />
                        </div>
                    </td>
                );
            })}
        </tr>
    );
}

export const Voting = <RowT extends VotingRow>(props: VotingProps<RowT>) => {
    const {
        headers,
        numberOfColumn,
        rows,
        secondRowLabels,
        thirdRowLabels,
        withMainRow = true,
        buttonColors,
        onSelectionChange,
        withSearchInput = false,
        onActiveChange,
        zeroUnitField = "unitvalue",

        infoColumns: infoColumnsProp,
        voteHeaders: voteHeadersProp,
        numberOfVoteColumns: numberOfVoteColumnsProp,
        columnLayout,

        mainRowLeftLabel = "Anwesend"
    } = props;

    const { t } = useTranslation();

    // Decide whether we are in "generic mode" (infoColumns provided)
    const isGeneric = !!(infoColumnsProp && infoColumnsProp.length > 0);

    // Back-compat: derive vote headers + vote columns
    const hasSecondLabels = !!(secondRowLabels && secondRowLabels.length > 0);
    const hasThirdLabels = !!(thirdRowLabels && thirdRowLabels.length > 0);
    // number of leading left-label columns (each consumes one entry from headers)
    const leftLabelCount = (hasSecondLabels ? 1 : 0) + (hasThirdLabels ? 1 : 0);

    const voteColumnsRaw = numberOfVoteColumnsProp ?? numberOfColumn ?? 0;
    const voteColumns = Math.min(Math.max(voteColumnsRaw, 1), 5);

    const derivedVoteHeaders = useMemo(() => {
        // generic mode: use voteHeadersProp directly
        if (voteHeadersProp && voteHeadersProp.length > 0) return voteHeadersProp.slice(0, voteColumns);

        // legacy mode: headers starts with one entry per left-label column, then vote headers
        if (headers && headers.length > 0) {
            return headers.slice(leftLabelCount, leftLabelCount + voteColumns);
        }

        return Array.from({ length: voteColumns }, (_, i) => `#${i + 1}`);
    }, [voteHeadersProp, voteColumns, headers, leftLabelCount]);

    // In legacy mode, left headers are blank/blank/(headers[0])
    // In generic mode, left headers are infoColumns headers
    const infoColumns = useMemo(() => {
        if (isGeneric) return infoColumnsProp!;
        return undefined;
    }, [isGeneric, infoColumnsProp]);

    const [searchTerm, setSearchTerm] = useState("");
    const filteredRows = useMemo(
        () => rows.filter((row) => row.label.toLowerCase().includes(searchTerm.toLowerCase())),
        [searchTerm, rows]
    );

    const onSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    }, []);

    // --- Column widths (colgroup) ---
    const colgroup = useMemo(() => {
        const cols: React.ReactNode[] = [];

        // active col
        cols.push(
            <col
                key="active"
                className={styles.colActive}
                style={columnLayout?.activeWidth ? { width: columnLayout.activeWidth } : undefined}
            />
        );

        if (isGeneric && infoColumns && infoColumns.length > 0) {
            const widths = columnLayout?.infoWidths;
            infoColumns.forEach((c, idx) => {
                const width = widths?.[idx] ?? c.width;
                cols.push(<col key={`info-${idx}`} style={width ? { width } : undefined} />);
            });
        } else {
            // legacy left widths from css
            if (hasSecondLabels) {
                const valueColClass = hasThirdLabels ? styles.colSecondNarrow : styles.colSecond;
                cols.push(<col key="label" className={styles.colLabel} />);
                cols.push(<col key="second" className={valueColClass} />);
                if (hasThirdLabels) {
                    cols.push(<col key="third" className={valueColClass} />);
                }
            } else {
                cols.push(<col key="labelWide" className={styles.colLabelWide} />);
            }
        }

        // vote cols
        const voteColStyle = columnLayout?.voteWidth ? { width: columnLayout.voteWidth } : undefined;
        cols.push(...Array.from({ length: voteColumns }, (_, i) => <col key={`vote-${i}`} style={voteColStyle} />));

        return cols;
    }, [columnLayout, hasSecondLabels, hasThirdLabels, infoColumns, isGeneric, voteColumns]);

    // --- Selected votes state (same as your original) ---
    const [selectedVotes, setSelectedVotes] = useState<(number | null)[]>(() =>
        rows.map((row) =>
            typeof row.selectedRow === "number" && row.selectedRow >= 1 && row.selectedRow <= 5
                ? row.selectedRow - 1
                : null
        )
    );

    // a row that is inactive or has a zero unitvalue/unitcount is read-only and must be excluded from votes
    const isRowSelectable = useCallback((row: RowT) => row.active && (row[zeroUnitField] ?? 0) !== 0, [zeroUnitField]);

    // clear selection if row becomes inactive or read-only
    useEffect(() => {
        let didChange = false;
        const updated = selectedVotes.map((vote, idx) => {
            if (!isRowSelectable(rows[idx]) && vote !== null) {
                didChange = true;
                return null;
            }
            return vote;
        });
        if (didChange) setSelectedVotes(updated);
    }, [rows, selectedVotes, isRowSelectable]);

    const lastResultRef = useRef<{ id: string; selectedColumn: number | null }[] | null>(null);
    useEffect(() => {
        if (onSelectionChange) {
            const result = rows.map((row, idx) => ({
                id: row.id,
                selectedColumn: selectedVotes[idx] !== null ? selectedVotes[idx]! + 1 : null
            }));
            const resultString = JSON.stringify(result);
            if (!lastResultRef.current || JSON.stringify(lastResultRef.current) !== resultString) {
                lastResultRef.current = result;
                onSelectionChange(result);
            }
        }
    }, [selectedVotes, onSelectionChange, rows]);

    const handleRowVote = useCallback((rowIndex: number, colIndex: number) => {
        if (scrollWrapperRef.current) {
            savedScrollTop.current = scrollWrapperRef.current.scrollTop;
        }
        setSelectedVotes((prev) => {
            const updated = [...prev];
            updated[rowIndex] = prev[rowIndex] === colIndex ? null : colIndex;
            return updated;
        });
    }, []);

    const handleMainRowVote = (colIndex: number) => {
        if (scrollWrapperRef.current) {
            savedScrollTop.current = scrollWrapperRef.current.scrollTop;
        }
        setSelectedVotes((prev) => {
            const activeIndices = rows.reduce<number[]>((acc, row, i) => {
                if (isRowSelectable(row)) acc.push(i);
                return acc;
            }, []);
            const allSelectedInColumn = activeIndices.every((i) => prev[i] === colIndex || prev[i] !== null);
            return prev.map((vote, i) => {
                if (!isRowSelectable(rows[i])) return vote;
                if (allSelectedInColumn) {
                    return vote === colIndex ? null : vote;
                } else {
                    return vote === null ? colIndex : vote;
                }
            });
        });
    };

    const isMainActive = (colIndex: number) => {
        const activeVotes = selectedVotes.filter((_, i) => rows[i] && isRowSelectable(rows[i]));
        return activeVotes.length > 0 && activeVotes.every((vote) => vote === colIndex);
    };

    const getButtonStyle = useCallback(
        (colIndex: number, isSelected: boolean): React.CSSProperties => {
            if (!isSelected || !buttonColors || buttonColors.length <= colIndex) return {};
            const color = buttonColors[colIndex];
            return { backgroundColor: color, borderColor: color, color: "#fff" };
        },
        [buttonColors]
    );

    const getBorderStyle = useCallback(
        (colIndex: number, isSelected: boolean): React.CSSProperties => {
            if (!isSelected || !buttonColors || buttonColors.length <= colIndex) return {};
            const color = buttonColors[colIndex];
            return { borderColor: color, backgroundColor: lightenColor(color, 0.8) };
        },
        [buttonColors]
    );

    const scrollWrapperRef = useRef<HTMLDivElement>(null);
    const savedScrollTop = useRef<number | null>(null);
    const [scrollbarWidth, setScrollbarWidth] = useState(0);

    useLayoutEffect(() => {
        if (savedScrollTop.current !== null && scrollWrapperRef.current) {
            scrollWrapperRef.current.scrollTop = savedScrollTop.current;
            savedScrollTop.current = null;
        }
    });

    useEffect(() => {
        const timeout = setTimeout(() => {
            const wrapper = scrollWrapperRef.current;
            if (!wrapper) return;
            const hasScrollbar = wrapper.scrollHeight > wrapper.clientHeight;
            const width = hasScrollbar ? wrapper.offsetWidth - wrapper.clientWidth : 0;
            setScrollbarWidth(width);
        }, 0);
        return () => clearTimeout(timeout);
    }, [filteredRows.length]);

    const sumOfSecondRowLabels = useMemo(() => {
        const sum = secondRowLabels?.reduce((acc, val) => acc + (parseFloat(val) || 0), 0) ?? 0;
        return Math.round(sum * 1000) / 1000;
    }, [secondRowLabels]);

    const sumOfThirdRowLabels = useMemo(() => {
        const sum = thirdRowLabels?.reduce((acc, val) => acc + (parseFloat(val) || 0), 0) ?? 0;
        return Math.round(sum * 1000) / 1000;
    }, [thirdRowLabels]);

    return (
        <div className={styles.votingWrapper}>
            {withSearchInput && (
                <div className={styles.searchRow}>
                    <UI.Input type="search" onChange={onSearch} placeholder="Suche..." value={searchTerm} />
                </div>
            )}

            {/* Sticky header table */}
            <div className={styles.votingHeaderWrapper} style={{ paddingRight: `${scrollbarWidth}px` }}>
                <table className={styles.votingTable}>
                    <colgroup>{colgroup}</colgroup>

                    <thead>
                        <tr>
                            {/* active header */}
                            <th className={styles.headerCell}></th>

                            {/* left headers */}
                            {isGeneric && infoColumns ? (
                                infoColumns.map((c, i) => (
                                    <th key={`h-info-${i}`} className={styles.headerCell}>
                                        {c.header}
                                    </th>
                                ))
                            ) : (
                                <>
                                    <th className={styles.headerCell}></th>
                                    {hasSecondLabels ? (
                                        <th className={styles.headerCell}>{headers?.[0] ?? ""}</th>
                                    ) : null}
                                    {hasThirdLabels ? (
                                        <th className={styles.headerCell}>{headers?.[1] ?? ""}</th>
                                    ) : null}
                                </>
                            )}

                            {/* vote headers */}
                            {derivedVoteHeaders.map((h, idx) => (
                                <th key={`h-vote-${idx}`} className={styles.headerCell}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {withMainRow && (
                            <tr className={styles.mainRow}>
                                {/* active col cell */}
                                <td
                                    className={`${styles.mainRowLabel} ${styles.mainRowLabelTruncated} ${styles.mainRowCell}`}
                                    title={mainRowLeftLabel}
                                >
                                    {mainRowLeftLabel}
                                </td>

                                {/* left area for main row */}
                                {isGeneric && infoColumns ? (
                                    infoColumns.map((_, i) => (
                                        <td
                                            key={`main-info-${i}`}
                                            className={`${styles.mainRowLabel} ${styles.mainRowCell}`}
                                        >
                                            {i === 0 ? t("labels.choose_all") : ""}
                                        </td>
                                    ))
                                ) : (
                                    <>
                                        <td className={`${styles.mainRowLabel} ${styles.mainRowCell}`}>
                                            {t("labels.choose_all")}
                                        </td>
                                        {hasSecondLabels && (
                                            <td className={`${styles.secondRowCell} ${styles.mainRowCell}`}>
                                                {sumOfSecondRowLabels}
                                            </td>
                                        )}
                                        {hasThirdLabels && (
                                            <td className={`${styles.secondRowCell} ${styles.mainRowCell}`}>
                                                {sumOfThirdRowLabels}
                                            </td>
                                        )}
                                    </>
                                )}

                                {/* main vote buttons */}
                                {Array.from({ length: voteColumns }).map((_, colIndex) => {
                                    const active = isMainActive(colIndex);
                                    return (
                                        <td
                                            key={`main-${colIndex}`}
                                            className={`${styles.tableCell} ${styles.mainRowCell}`}
                                        >
                                            <div
                                                className={styles.voteButtonBorder}
                                                style={getBorderStyle(colIndex, true)}
                                                onClick={() => handleMainRowVote(colIndex)}
                                                onMouseDown={(e) => e.preventDefault()}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <button
                                                    type="button"
                                                    className={`${styles.voteButton} ${active ? styles.active : ""}`}
                                                    style={getButtonStyle(colIndex, true)}
                                                    tabIndex={-1}
                                                />
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Scrollable body table */}
            <div className={styles.tableScrollWrapper} ref={scrollWrapperRef}>
                <table className={styles.votingTable}>
                    <colgroup>{colgroup}</colgroup>

                    <tbody>
                        {filteredRows.map((row) => {
                            const fullRowIndex = rows.findIndex((r) => r.id === row.id);
                            const isActive = row.active;
                            const selected = selectedVotes[fullRowIndex];
                            const secondLabel =
                                !isGeneric && hasSecondLabels ? secondRowLabels?.[fullRowIndex] : undefined;
                            const thirdLabel =
                                !isGeneric && hasThirdLabels ? thirdRowLabels?.[fullRowIndex] : undefined;

                            return (
                                <VotingRowItem
                                    key={row.id}
                                    row={row}
                                    fullRowIndex={fullRowIndex}
                                    voteColumns={voteColumns}
                                    selected={selected}
                                    secondLabel={secondLabel}
                                    thirdLabel={thirdLabel}
                                    isActive={isActive}
                                    handleRowVote={handleRowVote}
                                    getBorderStyle={getBorderStyle}
                                    getButtonStyle={getButtonStyle}
                                    onActiveChange={onActiveChange}
                                    zeroUnitField={zeroUnitField}
                                    infoColumns={infoColumns}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
